import type { FC } from 'react';
import { useCallback } from 'react';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useAddWishListItem } from '../../bff-api/customers/useAddWishListItem';
import { useDeleteWishListItem } from '../../bff-api/customers/useDeleteWishListItem';
import { useIsAddedToWishList } from '../../bff-api/customers/useIsAddedToWishList';
import { useCustomer } from '../../contexts/useCustomer';
import { isRegistered } from '../../contexts/utils/customer-utils';
import { useNavigateTo } from '../../hooks';
import { StorageKey } from '../../hooks/model/StorageKey';
import { useSLTToast } from '../../hooks/use-toast/useSLTToast';
import { PageUrl } from '../../pages/utils/page-url';
import { Button } from '../../ui-components';
import { useCustomPreferenceOrDefault, useUrl } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { GetIcon } from '../icons';

const messages = defineMessages({
    addedToWishlist: {
        id: 'wishlist.button.added',
        defaultMessage: 'Item has been added to your wishlist',
    },
    removedFromWishlist: {
        id: 'wishlist.button.removed',
        defaultMessage: 'Item has been removed from your wishlist',
    },
});

interface WishlistButtonProps {
    productId: string;
    wishListId?: string;
    className?: string;
    variant?: 'default' | 'circle';
    size?: 'sm' | 'md' | 'lg';
}

export const WishlistButton: FC<WishlistButtonProps> = ({
    productId,
    wishListId = 'default',
    className = '',
    variant = 'default',
    size = 'md',
}) => {
    const customer = useCustomer();
    const location = useLocation();
    const navigateTo = useNavigateTo();
    const loginShowUrl = useUrl(PageUrl.LOGIN_SHOW);
    const intl = useIntl();
    const showToast = useSLTToast();

    const isPwaAccountRedirectEnabled = useCustomPreferenceOrDefault(
        CustomPreference.PWA_ACCOUNT_REDIRECT,
        true,
    );

    const { data: isInWishlist, isLoading: isCheckingWishlist } = useIsAddedToWishList(productId);

    const addToWishlist = useAddWishListItem(wishListId);
    const removeFromWishlist = useDeleteWishListItem();

    const isLoading = isCheckingWishlist || addToWishlist.isLoading || removeFromWishlist.isLoading;
    const isReturningCustomer = isRegistered(customer.customer);

    const handleToggleWishlist = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            const currentUrl = encodeURIComponent(`${location.pathname}${location.search}`);
            const loginBaseUrl = isPwaAccountRedirectEnabled ? PageUrl.ACCOUNT_LOGIN : loginShowUrl;
            const loginUrl = `${loginBaseUrl}?redirect-url=${currentUrl}`;

            if (!isReturningCustomer) {
                // if the customer is not registered:
                // set the session variable to add the product to the wishlist
                sessionStorage.setItem(StorageKey.ADD_PRODUCT_ID_TO_WISHLIST, productId);
                // redirect to the login page
                navigateTo(loginUrl, {
                    useWindowLocation: !isPwaAccountRedirectEnabled,
                });
                return;
            }

            if (isLoading) {
                // if the wishlist is still loading, don't do anything
                return;
            }

            try {
                if (isInWishlist) {
                    await removeFromWishlist.mutateAsync(productId);
                    showToast(intl.formatMessage(messages.removedFromWishlist));
                } else {
                    await addToWishlist.mutateAsync(productId);
                    showToast(intl.formatMessage(messages.addedToWishlist));
                }
            } catch {
                /* empty */
            }
        },
        [
            location.pathname,
            location.search,
            isPwaAccountRedirectEnabled,
            loginShowUrl,
            isReturningCustomer,
            isLoading,
            navigateTo,
            isInWishlist,
            removeFromWishlist,
            productId,
            addToWishlist,
            intl,
            showToast,
        ],
    );

    const renderButtonContent = useCallback((): React.ReactNode => {
        if (!isReturningCustomer) {
            return <GetIcon name="wishlist" size={size} />;
        }
        if (isLoading) {
            return <Spinner size="xs" color="slt-pepper.1000" aria-hidden="true" />;
        }
        return <GetIcon name={isInWishlist ? 'wishlist-filled' : 'wishlist'} size={size} />;
    }, [isReturningCustomer, isLoading, isInWishlist, size]);

    return (
        <Flex
            alignItems="center"
            justifyContent="center"
            data-testid={`wishlist-container-${productId}`}
        >
            {variant === 'circle' ? (
                <Box
                    as="button"
                    id={`wishlist-button-${productId}`}
                    onClick={handleToggleWishlist}
                    disabled={isLoading && isReturningCustomer}
                    className={className}
                    data-testid={`wishlist-button-${productId}`}
                    aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                    aria-pressed={isInWishlist}
                    width={getCircleSize(size)}
                    height={getCircleSize(size)}
                    borderRadius="50%"
                    backgroundColor="slt-primary-green.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{ backgroundColor: 'slt-primary-green.200' }}
                    _active={{ backgroundColor: 'slt-primary-green.300' }}
                    transition="background-color 0.2s"
                >
                    {renderButtonContent()}
                </Box>
            ) : (
                <Button
                    id={`wishlist-button-${productId}`}
                    variant="text-button-no-hover"
                    onClick={handleToggleWishlist}
                    isDisabled={isLoading && isReturningCustomer}
                    className={className}
                    data-testid={`wishlist-button-${productId}`}
                    aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                    aria-pressed={isInWishlist}
                >
                    {renderButtonContent()}
                </Button>
            )}
        </Flex>
    );
};

WishlistButton.defaultProps = {
    className: undefined,
    wishListId: 'default',
    variant: 'default',
    size: 'md',
};

const getCircleSize = (size: 'sm' | 'md' | 'lg'): string => {
    switch (size) {
        case 'sm':
            return '24px';
        case 'md':
            return '32px';
        case 'lg':
            return '40px';
        default:
            return '32px';
    }
};
